import { useState } from 'react';
import { X } from 'lucide-react';
import { addTag, MAX_TAG_LENGTH, MAX_TAGS_PER_SONG, normalizeTag, removeTag, suggestTags } from '../../utils/songTags';

interface SongTagEditorProps {
  tags: string[];
  allTags: string[];
  saving: boolean;
  onChange: (next: string[]) => Promise<void>;
}

export function SongTagEditor({ tags, allTags, saving, onChange }: SongTagEditorProps) {
  const [inputText, setInputText] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);

  const suggestions = inputText.trim() ? suggestTags(allTags, inputText, tags) : [];

  async function handleAdd(raw: string) {
    const next = addTag(tags, raw);
    if (next === null) {
      setRefusal(normalizeTag(raw) === null ? `Tags are 1–${MAX_TAG_LENGTH} characters` : `A song can have up to ${MAX_TAGS_PER_SONG} tags`);
      return;
    }
    setRefusal(null);
    setInputText('');
    if (next.length !== tags.length) await onChange(next);
  }

  return (
    <div className="space-y-2">
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map(tag => (
            <span
              key={tag}
              className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full flex items-center gap-1"
            >
              {tag}
              <button
                type="button"
                onClick={() => onChange(removeTag(tags, tag))}
                disabled={saving}
                aria-label={`Remove tag ${tag}`}
                className="hover:text-purple-900 dark:hover:text-purple-100 disabled:opacity-50"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        value={inputText}
        onChange={e => {
          setInputText(e.target.value);
          setRefusal(null);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd(inputText);
          }
        }}
        disabled={saving}
        maxLength={MAX_TAG_LENGTH + 1}
        placeholder="Add a tag…"
        className="input text-sm w-full"
      />
      {refusal && <p className="text-xs text-red-600 dark:text-red-400">{refusal}</p>}
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map(suggestion => (
            <button
              key={suggestion}
              type="button"
              onClick={() => handleAdd(suggestion)}
              disabled={saving}
              className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full hover:bg-purple-100 dark:hover:bg-purple-900/30 disabled:opacity-50"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
